import React, { useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { HiArrowDown } from 'react-icons/hi';
import { FaCode, FaHardHat, FaArrowRight } from 'react-icons/fa';

import { useTheme } from '../../context/ThemeContext';
import './Hero.css';

const Hero = ({ setDivision }) => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  const getParticleColor = useCallback(() => {
    const style = getComputedStyle(document.documentElement);
    return style.getPropertyValue('--particle-color').trim() || '122, 185, 52';
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    let particleColor = getParticleColor();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Re-read color when theme changes
    particleColor = getParticleColor();

    class Particle {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.opacity = Math.random() * 0.5 + 0.1;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) {
          this.reset();
        }
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${particleColor}, ${this.opacity})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < 80; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${particleColor}, ${0.06 * (1 - dist / 120)})`;            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [theme, getParticleColor]);

  return (
    <section className="hero" id="home">
      <canvas ref={canvasRef} className="hero__canvas" />
      <div className="hero__gradient-orbs">
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="hero__orb hero__orb--3" />
        <div className="hero__orb hero__orb--4" />
        <div className="hero__orb hero__orb--5" />
      </div>

      <div className="hero__content">
        <motion.div
          className="hero__badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <span className="hero__badge-dot" />
          Building the Future of Sri Lanka
        </motion.div>

        <motion.h1
          className="hero__title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Software That{' '}
          <span className="hero__title-highlight">Powers</span>
          <br />
          Your{' '}
          <span className="hero__title-highlight">Business</span>
        </motion.h1>

        <motion.p
          className="hero__subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          KAT99 Pvt Ltd — From mobile apps to enterprise web platforms, we
          design, build and deliver software that grows with your business.
        </motion.p>

        {/* Our Groups teaser */}
        <motion.div
          className="hero__groups"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
        >
          <p className="hero__groups-label">
            <span className="hero__groups-label-dot" /> KAT99 Group of Companies
          </p>
          <div className="hero__groups-cards">
            <div className="hero__groups-card hero__groups-card--active">
              <div className="hero__groups-card-icon hero__groups-card-icon--software">
                <FaCode />
              </div>
              <div className="hero__groups-card-body">
                <span className="hero__groups-card-tag">You are here</span>
                <h3>KAT99 Software</h3>
                <p>Apps · Web · Digital Solutions</p>
              </div>
            </div>
            <button
              className="hero__groups-card hero__groups-card--construction"
              onClick={() => setDivision('construction')}
            >
              <div className="hero__groups-card-icon hero__groups-card-icon--construction">
                <FaHardHat />
              </div>
              <div className="hero__groups-card-body">
                <span className="hero__groups-card-tag">Visit Division</span>
                <h3>KAT99 Construction</h3>
                <p>Woodworks · Renovations · Building</p>
              </div>
              <FaArrowRight className="hero__groups-card-arrow" />
            </button>
          </div>
        </motion.div>

        <motion.div
          className="hero__stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.0 }}
        >
          <div className="hero__stat">
            <span className="hero__stat-number">2,100+</span>
            <span className="hero__stat-label">Projects Done</span>
          </div>
          <div className="hero__stat-divider" />
          <div className="hero__stat">
            <span className="hero__stat-number">3,000+</span>
            <span className="hero__stat-label">Happy Clients</span>
          </div>
          <div className="hero__stat-divider" />
          <div className="hero__stat">
            <span className="hero__stat-number">30+</span>
            <span className="hero__stat-label">Years Active</span>
          </div>
          <div className="hero__stat-divider" />
          <div className="hero__stat">
            <span className="hero__stat-number">100%</span>
            <span className="hero__stat-label">Commitment</span>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="hero__scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
      >
        <a href="#about" className="hero__scroll-btn">
          <HiArrowDown className="hero__scroll-icon" />
        </a>
      </motion.div>
    </section>
  );
};

export default Hero;
